import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { QueueItem } from './db'
import { uploadQueue } from './queue'

export interface QueueStats {
  /** Alles wat deze sessie in de wachtrij is gezet, inclusief wat al klaar is. */
  total: number

  done: number
  failed: number

  /** Bezig of wachtend; wat er nog moet gebeuren. */
  pending: number

  /** 0 tot 1 over de hele wachtrij, gewogen naar bestandsgrootte. */
  progress: number

  /** Seconden, of null als er nog niets te schatten valt. */
  etaSeconds: number | null

  /** Er is werk in uitvoering; de UI toont dan de banner en waarschuwt bij afsluiten. */
  busy: boolean
}

export interface QueueView {
  items: QueueItem[]
  uploading: QueueItem[]
  queued: QueueItem[]
  failed: QueueItem[]
  done: QueueItem[]
  stats: QueueStats
}

/**
 * De wachtrij als React-state.
 *
 * `useSyncExternalStore` en geen context met een reducer: de wachtrij bestaat
 * ook als er geen component naar kijkt, en dat is precies het punt — hij loopt
 * door terwijl je in de galerij scrollt.
 */
export function useUploadQueue(slug: string, guestId: string | null): QueueView {
  const items = useSyncExternalStore(uploadQueue.subscribe, uploadQueue.getSnapshot)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (guestId) {
      void uploadQueue.activate({ slug, guestId })
    }
  }, [slug, guestId])

  const doneCount = items.filter((item) => item.status === 'done').length

  // Een geslaagde upload staat nu in het album. Zonder dit blijft de galerij
  // de oude pagina tonen tot de gast hem zelf ververst.
  useEffect(() => {
    if (doneCount > 0) {
      void queryClient.invalidateQueries({ queryKey: ['media', slug] })
      void queryClient.invalidateQueries({ queryKey: ['event', slug] })
      void queryClient.invalidateQueries({ queryKey: ['guest', slug] })
      void queryClient.invalidateQueries({ queryKey: ['challenges', slug] })
    }
  }, [doneCount, slug, queryClient])

  return useMemo(() => view(items), [items])
}

function view(items: QueueItem[]): QueueView {
  const uploading = items.filter(
    (item) => item.status === 'uploading' || item.status === 'preparing',
  )

  const queued = items.filter((item) => item.status === 'queued')
  const failed = items.filter((item) => item.status === 'failed')
  const done = items.filter((item) => item.status === 'done')

  const active = [...uploading, ...queued]

  // Op bytes en niet op aantal: acht foto's plus één video van 200 MB is niet
  // "acht van negen" als die video nog moet.
  const totalBytes = items.reduce((sum, item) => sum + item.sizeBytes, 0)

  const sentBytes = items.reduce(
    (sum, item) => sum + item.sizeBytes * (item.status === 'done' ? 1 : item.progress),
    0,
  )

  const rate = uploadQueue.getRate()
  const remaining = Math.max(0, totalBytes - sentBytes)

  return {
    items,
    uploading,
    queued,
    failed,
    done,
    stats: {
      total: items.length,
      done: done.length,
      failed: failed.length,
      pending: active.length,
      progress: totalBytes > 0 ? Math.min(1, sentBytes / totalBytes) : 0,
      etaSeconds: rate && rate > 0 && active.length > 0 ? remaining / rate : null,
      busy: active.length > 0,
    },
  }
}
